/**
 * AuditLog.js - Audit Log Viewer (Admin Only)
 * 
 * Displays a filterable, searchable history of all system actions including
 * user management, schedule changes, PTO actions, room modifications, and student events.
 * 
 * Features:
 * - Filter by action type, actor, date range
 * - Search within descriptions
 * - Expandable detail view for each entry
 * - Color-coded action categories
 * - Pagination/load more
 * - CSV export with all details
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import dataStore, { AUDIT_ACTIONS } from '../data/dataStore';

function AuditLog() {
  const { theme, darkMode, colors } = useTheme();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  
  // State for audit entries and filters
  const [auditEntries, setAuditEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [displayCount, setDisplayCount] = useState(50);
  const [expandedId, setExpandedId] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    actionType: '',
    actorId: '',
    searchText: '',
    startDate: '',
    endDate: ''
  });
  
  // Get unique actors for filter dropdown
  const [actors, setActors] = useState([]);
  
  // Load audit entries on mount - instant from cache, then refresh
  useEffect(() => {
    // Immediately load from cache (instant)
    const cachedEntries = dataStore.getAuditLog({ limit: 2000 });
    setAuditEntries(cachedEntries);
    extractActors(cachedEntries);
    
    // Then refresh from database in background
    setIsLoading(true);
    dataStore.refreshAuditLog(2000).then(freshEntries => {
      setAuditEntries(freshEntries);
      extractActors(freshEntries);
      setIsLoading(false);
    }).catch(err => {
      console.error('Error refreshing audit log:', err);
      setIsLoading(false);
    });
  }, []);
  
  const extractActors = (entries) => {
    const uniqueActors = [...new Map(
      entries.map(e => [e.actorId, { id: e.actorId, name: e.actorName }])
    ).values()];
    setActors(uniqueActors);
  };

  // Group action types by category for filtering
  const actionCategories = {
    'Authentication': [
      AUDIT_ACTIONS.USER_LOGIN,
      AUDIT_ACTIONS.USER_LOGOUT,
      AUDIT_ACTIONS.LOGIN_FAILED
    ],
    'User Management': [
      AUDIT_ACTIONS.USER_CREATED,
      AUDIT_ACTIONS.USER_UPDATED,
      AUDIT_ACTIONS.USER_DELETED,
      AUDIT_ACTIONS.PASSWORD_RESET,
      AUDIT_ACTIONS.PASSWORD_CHANGED,
      AUDIT_ACTIONS.AVAILABILITY_UPDATED
    ],
    'Room Management': [
      AUDIT_ACTIONS.ROOM_CREATED,
      AUDIT_ACTIONS.ROOM_UPDATED,
      AUDIT_ACTIONS.ROOM_DELETED
    ],
    'Teacher Schedule': [
      AUDIT_ACTIONS.SCHEDULE_ASSIGNMENT_ADDED,
      AUDIT_ACTIONS.SCHEDULE_ASSIGNMENT_REMOVED,
      AUDIT_ACTIONS.SCHEDULE_CLEARED,
      AUDIT_ACTIONS.SCHEDULE_PUBLISHED,
      AUDIT_ACTIONS.SCHEDULE_UNPUBLISHED
    ],
    'PTO Requests': [
      AUDIT_ACTIONS.PTO_REQUESTED,
      AUDIT_ACTIONS.PTO_APPROVED,
      AUDIT_ACTIONS.PTO_DENIED,
      AUDIT_ACTIONS.PTO_CANCELLED
    ],
    'Student Management': [
      AUDIT_ACTIONS.CHILD_CREATED,
      AUDIT_ACTIONS.CHILD_UPDATED,
      AUDIT_ACTIONS.CHILD_DELETED,
      AUDIT_ACTIONS.CHILD_SCHEDULED,
      AUDIT_ACTIONS.CHILD_UNSCHEDULED,
      AUDIT_ACTIONS.CHILD_SCHEDULE_CLEARED,
      AUDIT_ACTIONS.CHILD_VISIT_SCHEDULED,
      AUDIT_ACTIONS.CHILD_MARKED_ABSENT,
      AUDIT_ACTIONS.CHILD_MARKED_PRESENT
    ],
    'Templates': [
      AUDIT_ACTIONS.TEMPLATE_CREATED,
      AUDIT_ACTIONS.TEMPLATE_UPDATED,
      AUDIT_ACTIONS.TEMPLATE_DELETED,
      AUDIT_ACTIONS.TEMPLATE_APPLIED
    ]
  };
  
  // Get category and color for an action
  const getActionStyle = (action) => {
    if (!action) {
      return { category: 'Other', color: theme.textSecondary, bg: theme.border };
    }
    if (actionCategories['Authentication'].includes(action)) {
      return { category: 'Auth', color: '#8e44ad', bg: '#8e44ad20' };
    }
    if (actionCategories['User Management'].includes(action)) {
      return { category: 'User', color: colors.blue, bg: `${colors.blue}20` };
    }
    if (actionCategories['Room Management'].includes(action)) {
      return { category: 'Room', color: colors.purple || '#9b59b6', bg: '#9b59b620' };
    }
    if (actionCategories['Teacher Schedule'].includes(action)) {
      return { category: 'Schedule', color: colors.orange || '#e67e22', bg: '#e67e2220' };
    }
    if (actionCategories['PTO Requests'].includes(action)) {
      return { category: 'PTO', color: colors.green, bg: `${colors.green}20` };
    }
    if (actionCategories['Student Management'].includes(action)) {
      return { category: 'Student', color: '#3498db', bg: '#3498db20' };
    }
    if (actionCategories['Templates'].includes(action)) {
      return { category: 'Template', color: '#16a085', bg: '#16a08520' };
    }
    return { category: 'Other', color: theme.textSecondary, bg: theme.border };
  };
  
  // Format action name for display - user-friendly labels
  const formatActionName = (action) => {
    const actionLabels = {
      // Authentication
      'USER_LOGIN': 'Logged In',
      'USER_LOGOUT': 'Logged Out',
      'LOGIN_FAILED': 'Failed Login',
      
      // User Management
      'USER_CREATED': 'User Created',
      'USER_UPDATED': 'User Updated',
      'USER_DELETED': 'User Deleted',
      'PASSWORD_RESET': 'Password Reset',
      'PASSWORD_CHANGED': 'Password Changed',
      'AVAILABILITY_UPDATED': 'Availability Changed',
      
      // Room Management
      'ROOM_CREATED': 'Room Created',
      'ROOM_UPDATED': 'Room Updated',
      'ROOM_DELETED': 'Room Deleted',
      
      // Schedule Management
      'SCHEDULE_ASSIGNMENT_ADDED': 'Teacher Assigned',
      'SCHEDULE_ASSIGNMENT_REMOVED': 'Teacher Unassigned',
      'SCHEDULE_CLEARED': 'Schedule Cleared',
      'SCHEDULE_PUBLISHED': 'Schedule Published',
      'SCHEDULE_UNPUBLISHED': 'Schedule Unpublished',
      
      // PTO Management
      'PTO_REQUESTED': 'PTO Requested',
      'PTO_APPROVED': 'PTO Approved',
      'PTO_DENIED': 'PTO Denied',
      'PTO_CANCELLED': 'PTO Cancelled',
      
      // Student Management
      'CHILD_CREATED': 'Student Enrolled',
      'CHILD_UPDATED': 'Student Updated',
      'CHILD_DELETED': 'Student Removed',
      'CHILD_SCHEDULED': 'Student Scheduled',
      'CHILD_UNSCHEDULED': 'Student Unscheduled',
      'CHILD_SCHEDULE_CLEARED': 'Student Schedule Cleared',
      'CHILD_VISIT_SCHEDULED': 'Visit Scheduled',
      'CHILD_MARKED_ABSENT': 'Student Marked Absent',
      'CHILD_MARKED_PRESENT': 'Student Marked Present',
      
      // Template Management
      'TEMPLATE_CREATED': 'Template Created',
      'TEMPLATE_UPDATED': 'Template Updated',
      'TEMPLATE_DELETED': 'Template Deleted',
      'TEMPLATE_APPLIED': 'Template Applied'
    };
    
    return actionLabels[action] || 'Other Action';
  };
  
  // Format detail key for display - user-friendly labels
  const formatDetailKey = (key) => {
    const labelMap = {
      // User-friendly labels for display
      'student': 'Student',
      'teacher': 'Teacher',
      'room': 'Room',
      'date': 'Date',
      'dateRange': 'Date Range',
      'time': 'Time',
      'type': 'Type',
      'reason': 'Reason',
      'changes': 'Changes',
      'name': 'Name',
      'username': 'Username',
      'role': 'Role',
      'status': 'Status',
      'dates': 'Dates',
      'requestedBy': 'Requested By',
      'partialDay': 'Partial Day',
      'action': 'Action',
      'added': 'Added',
      'skipped': 'Skipped',
      'studentsProcessed': 'Students Processed',
      'studentsCleared': 'Students Cleared',
      'weekStart': 'Week Of',
      
      // Login/Auth fields
      'ip': 'IP Address',
      'os': 'Operating System',
      'browser': 'Browser',
      'device': 'Device',
      
      // Hidden fields - return null to skip display
      'targetName': null,
      'userName': null,
      'childName': null,
      'roomName': null,
      'templateName': null,
      'description': null,
      'roomId': null,
      'userId': null,
      'childId': null,
      'assignmentId': null,
      'targetId': null,
      'darkMode': null,
      'headerColor': null,
      'accentColor': null,
      'textColor': null,
      'borderColor': null,
      'customHours': null,
      'customHoursStart': null,
      'customHoursEnd': null,
      'useCustomHours': null,
      'parentContact': null,
      'parentName': null,
      'parentPhone': null,
      'parentEmail': null,
      'displayOrder': null,
      'isVisible': null,
      'availability': null,
      'password': null,
      'createdAt': null,
      'updatedAt': null,
      'startDate': null,
      'endDate': null,
      'startTime': null,
      'endTime': null,
      'childCount': null,
      'entriesAdded': null,
      'entriesSkipped': null,
      'overwrite': null
    };
    if (labelMap.hasOwnProperty(key)) return labelMap[key];
    // Convert camelCase to Title Case for unknown keys
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };
  
  // Helper to convert YYYY-MM-DD to MM/DD/YYYY
  const formatDateString = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return dateStr;
    // Match YYYY-MM-DD pattern
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      return `${match[2]}/${match[3]}/${match[1]}`;
    }
    return dateStr;
  };
  
  // Format detail value for display
  const formatDetailValue = (key, value) => {
    if (value === null || value === undefined) return '—';
    
    // Handle date fields
    if (key === 'date' || key === 'dateRange' || key === 'weekStart') {
      // Single date or date range - convert any YYYY-MM-DD patterns
      return String(value).replace(/(\d{4})-(\d{2})-(\d{2})/g, '$2/$3/$1');
    }
    
    // Handle dates field (comma-separated dates)
    if (key === 'dates' && typeof value === 'string') {
      return value.split(', ').map(d => formatDateString(d.trim())).join(', ');
    }
    
    if (Array.isArray(value)) {
      // Check if array contains date strings
      return value.map(v => formatDateString(v)).join(', ');
    }
    
    if (typeof value === 'object') return JSON.stringify(value);
    
    if (key === 'role') {
      const roleLabels = { 'ADMIN': 'Administrator', 'USER': 'Teacher' };
      return roleLabels[value] || value;
    }
    
    return String(value);
  };
  
  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Filter entries based on current filter state
  const filteredEntries = useMemo(() => {
    return auditEntries.filter(entry => {
      // Filter by action type (supports both individual actions and categories)
      if (filters.actionType) {
        if (filters.actionType.startsWith('CATEGORY:')) {
          // Category filter - get all actions in this category
          const categoryName = filters.actionType.replace('CATEGORY:', '');
          const categoryActions = actionCategories[categoryName] || [];
          if (!categoryActions.includes(entry.action)) {
            return false;
          }
        } else {
          // Individual action filter
          if (entry.action !== filters.actionType) {
            return false;
          }
        }
      }
      
      // Filter by actor
      if (filters.actorId && entry.actorId !== filters.actorId) {
        return false;
      }
      
      // Filter by search text
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        const matchesDescription = entry.description?.toLowerCase().includes(searchLower);
        const matchesActor = entry.actorName?.toLowerCase().includes(searchLower);
        const matchesDetails = JSON.stringify(entry.details || {}).toLowerCase().includes(searchLower);
        if (!matchesDescription && !matchesActor && !matchesDetails) {
          return false;
        }
      }
      
      // Filter by start date
      if (filters.startDate) {
        const entryDate = new Date(entry.timestamp);
        const startDate = new Date(filters.startDate);
        if (entryDate < startDate) return false;
      }
      
      // Filter by end date
      if (filters.endDate) {
        const entryDate = new Date(entry.timestamp);
        const endDate = new Date(filters.endDate + 'T23:59:59');
        if (entryDate > endDate) return false;
      }
      
      return true;
    });
  }, [auditEntries, filters]);
  
  // Entries to display (with pagination)
  const displayedEntries = filteredEntries.slice(0, displayCount);
  const hasMore = filteredEntries.length > displayCount;
  
  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setDisplayCount(50); // Reset pagination when filters change
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      actionType: '',
      actorId: '',
      searchText: '',
      startDate: '',
      endDate: ''
    });
  };
  
  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  // Export to CSV
  const handleExportCsv = () => {
    // Create CSV headers
    const headers = [
      'ID',
      'Timestamp',
      'Action',
      'Category',
      'Actor ID',
      'Actor Name',
      'Target Type',
      'Target ID',
      'Description',
      'Details'
    ];
    
    // Create CSV rows from filtered entries
    const rows = filteredEntries.map(entry => {
      const actionStyle = getActionStyle(entry.action);
      return [
        entry.id,
        new Date(entry.timestamp).toISOString(),
        entry.action,
        actionStyle.category,
        entry.actorId,
        entry.actorName,
        entry.targetType || '',
        entry.targetId || '',
        entry.description?.replace(/"/g, '""') || '', // Escape quotes
        JSON.stringify(entry.details || {}).replace(/"/g, '""') // Escape quotes in JSON
      ];
    });
    
    // Build CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Generate filename with date range
    const dateStr = new Date().toISOString().split('T')[0];
    let filename = `audit-log-${dateStr}`;
    if (filters.startDate) filename += `-from-${filters.startDate}`;
    if (filters.endDate) filename += `-to-${filters.endDate}`;
    filename += '.csv';
    
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // =============================================
  // STYLES
  // =============================================
  
  const containerStyle = {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: isMobile ? '12px' : '24px'
  };
  
  const headerBannerStyle = {
    background: darkMode 
      ? 'linear-gradient(135deg, rgba(30,30,50,0.95), rgba(40,40,60,0.9))'
      : 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(245,245,250,0.9))',
    backdropFilter: 'blur(10px)',
    borderRadius: isMobile ? '12px' : '16px',
    padding: isMobile ? '16px' : '24px',
    marginBottom: isMobile ? '16px' : '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  };
  
  const titleRowStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'flex-start' : 'center',
    gap: isMobile ? '12px' : '0',
    marginBottom: '20px'
  };
  
  const titleStyle = {
    fontSize: isMobile ? '1.25rem' : '1.5rem',
    fontWeight: '600',
    color: theme.text,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };
  
  const statsStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? '8px' : '20px',
    fontSize: '0.85rem',
    color: theme.textSecondary,
    alignItems: isMobile ? 'flex-start' : 'center'
  };
  
  const filterRowStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    flexWrap: 'wrap',
    gap: '12px',
    alignItems: isMobile ? 'stretch' : 'center'
  };
  
  const filterInputStyle = {
    padding: isMobile ? '12px' : '8px 12px',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.cardBackground,
    color: theme.text,
    fontSize: isMobile ? '1rem' : '0.85rem',
    width: isMobile ? '100%' : 'auto',
    minWidth: isMobile ? 'auto' : '150px',
    boxSizing: 'border-box'
  };
  
  const searchInputStyle = {
    ...filterInputStyle,
    minWidth: isMobile ? 'auto' : '250px'
  };
  
  const dateFilterContainerStyle = {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    width: isMobile ? '100%' : 'auto'
  };
  
  const dateInputStyle = {
    ...filterInputStyle,
    flex: isMobile ? 1 : 'none',
    minWidth: isMobile ? '0' : '150px'
  };
  
  const buttonStyle = {
    padding: isMobile ? '12px 16px' : '8px 16px',
    borderRadius: '8px',
    border: 'none',
    fontSize: isMobile ? '1rem' : '0.85rem',
    cursor: 'pointer',
    fontWeight: '500',
    width: isMobile ? '100%' : 'auto'
  };
  
  const clearButtonStyle = {
    ...buttonStyle,
    backgroundColor: theme.border,
    color: theme.text,
    display: hasActiveFilters ? 'block' : 'none'
  };
  
  const downloadButtonStyle = {
    ...buttonStyle,
    backgroundColor: colors.green,
    color: darkMode ? '#1a1a2e' : '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px'
  };

  const entryListStyle = {
    backgroundColor: theme.cardBackground,
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    overflow: 'hidden'
  };
  
  const entryStyle = (isExpanded) => ({
    padding: isMobile ? '12px 14px' : '16px 20px',
    borderBottom: `1px solid ${theme.border}`,
    cursor: 'pointer',
    backgroundColor: isExpanded ? (darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)') : 'transparent',
    transition: 'background-color 0.2s'
  });
  
  const entryHeaderStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'flex-start' : 'flex-start',
    gap: isMobile ? '8px' : '12px'
  };
  
  const entryTopRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    justifyContent: 'space-between'
  };
  
  const actionBadgeStyle = (actionStyle) => ({
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.7rem',
    fontWeight: '600',
    backgroundColor: actionStyle.bg,
    color: actionStyle.color,
    whiteSpace: 'nowrap'
  });
  
  const entryContentStyle = {
    flex: 1,
    minWidth: 0,
    width: isMobile ? '100%' : 'auto'
  };
  
  const descriptionStyle = {
    fontSize: isMobile ? '0.9rem' : '0.9rem',
    color: theme.text,
    marginBottom: '4px',
    lineHeight: 1.4
  };
  
  const metaRowStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: isMobile ? '8px' : '16px',
    fontSize: isMobile ? '0.75rem' : '0.8rem',
    color: theme.textSecondary
  };
  
  const timestampStyle = {
    fontSize: isMobile ? '0.75rem' : '0.8rem',
    color: theme.textSecondary,
    whiteSpace: 'nowrap'
  };
  
  const expandedDetailsStyle = {
    marginTop: '12px',
    padding: isMobile ? '10px' : '12px',
    backgroundColor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)',
    borderRadius: '8px',
    fontSize: isMobile ? '0.8rem' : '0.8rem'
  };
  
  const detailRowStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? '2px' : '8px',
    marginBottom: isMobile ? '10px' : '6px'
  };
  
  const detailLabelStyle = {
    fontWeight: '600',
    color: theme.textSecondary,
    minWidth: isMobile ? 'auto' : '100px',
    fontSize: isMobile ? '0.75rem' : '0.8rem'
  };
  
  const detailValueStyle = {
    color: theme.text,
    wordBreak: 'break-word'
  };
  
  const loadMoreStyle = {
    padding: '16px',
    textAlign: 'center',
    borderTop: `1px solid ${theme.border}`
  };
  
  const loadMoreButtonStyle = {
    padding: '10px 24px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: colors.blue,
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer'
  };
  
  const emptyStateStyle = {
    padding: '60px 20px',
    textAlign: 'center',
    color: theme.textSecondary
  };

  // =============================================
  // RENDER
  // =============================================
  
  return (
    <div style={containerStyle}>
      {/* Header Banner with Filters */}
      <div style={headerBannerStyle}>
        <div style={titleRowStyle}>
          <h1 style={titleStyle}>
            <span>📋</span>
            Audit Log
          </h1>
          <div style={statsStyle}>
            <span>Showing {displayedEntries.length} of {filteredEntries.length}</span>
            {!isMobile && <span>•</span>}
            <span>{auditEntries.length} total{isLoading && ' (loading...)'}</span>
            <button style={downloadButtonStyle} onClick={handleExportCsv} title="Download filtered entries as CSV">
              <span>📥</span> {isMobile ? 'CSV' : 'Download CSV'}
            </button>
          </div>
        </div>
        
        {/* Filters */}
        <div style={filterRowStyle}>
          {/* Search */}
          <input
            type="text"
            placeholder="🔍 Search..."
            value={filters.searchText}
            onChange={(e) => handleFilterChange('searchText', e.target.value)}
            style={searchInputStyle}
          />
          
          {/* Action Type Filter */}
          <select
            value={filters.actionType}
            onChange={(e) => handleFilterChange('actionType', e.target.value)}
            style={filterInputStyle}
          >
            <option value="">All Actions</option>
            {Object.entries(actionCategories).map(([category, actions]) => (
              <optgroup key={category} label={category}>
                <option value={`CATEGORY:${category}`} style={{ fontWeight: 'bold' }}>
                  ▸ All {category}
                </option>
                {actions.map(action => (
                  <option key={action} value={action}>
                    &nbsp;&nbsp;{formatActionName(action)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          
          {/* Actor Filter */}
          <select
            value={filters.actorId}
            onChange={(e) => handleFilterChange('actorId', e.target.value)}
            style={filterInputStyle}
          >
            <option value="">All Users</option>
            {actors.map(actor => (
              <option key={actor.id} value={actor.id}>
                {actor.name}
              </option>
            ))}
          </select>
          
          {/* Date Range */}
          <div style={dateFilterContainerStyle}>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              style={dateInputStyle}
              title="Start Date"
            />
            <span style={{ color: theme.textSecondary, flexShrink: 0 }}>to</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              style={dateInputStyle}
              title="End Date"
            />
          </div>
          
          {/* Clear Filters */}
          <button style={clearButtonStyle} onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Entry List */}
      <div style={entryListStyle}>
        {displayedEntries.length === 0 ? (
          <div style={emptyStateStyle}>
            <p style={{ fontSize: '2rem', marginBottom: '10px' }}>📭</p>
            <p style={{ fontSize: '1.1rem', marginBottom: '5px' }}>No audit entries found</p>
            <p style={{ fontSize: '0.9rem' }}>
              {hasActiveFilters ? 'Try adjusting your filters' : 'Activity will appear here as users interact with the system'}
            </p>
          </div>
        ) : (
          <>
            {displayedEntries.map(entry => {
              const actionStyle = getActionStyle(entry.action);
              const isExpanded = expandedId === entry.id;
              
              return (
                <div 
                  key={entry.id}
                  style={entryStyle(isExpanded)}
                  onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                >
                  <div style={entryHeaderStyle}>
                    {/* Mobile: Badge and Timestamp on same row */}
                    {isMobile ? (
                      <>
                        <div style={entryTopRowStyle}>
                          <span style={actionBadgeStyle(actionStyle)}>
                            {actionStyle.category}
                          </span>
                          <div style={timestampStyle}>
                            {formatTimestamp(entry.timestamp)}
                          </div>
                        </div>
                        <div style={entryContentStyle}>
                          <div style={descriptionStyle}>
                            {entry.description?.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$2/$3/$1')}
                          </div>
                          <div style={metaRowStyle}>
                            <span>👤 {entry.actorName}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Desktop: Original layout */}
                        <span style={actionBadgeStyle(actionStyle)}>
                          {actionStyle.category}
                        </span>
                        <div style={entryContentStyle}>
                          <div style={descriptionStyle}>
                            {entry.description?.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$2/$3/$1')}
                          </div>
                          <div style={metaRowStyle}>
                            <span>👤 {entry.actorName}</span>
                            <span>•</span>
                            <span style={{ opacity: 0.8 }}>{formatActionName(entry.action)}</span>
                          </div>
                        </div>
                        <div style={timestampStyle}>
                          {formatTimestamp(entry.timestamp)}
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div style={expandedDetailsStyle}>
                      {/* Timestamp - Full */}
                      <div style={detailRowStyle}>
                        <span style={detailLabelStyle}>Timestamp:</span>
                        <span style={detailValueStyle}>{new Date(entry.timestamp).toLocaleString()}</span>
                      </div>
                      
                      {/* Performed By */}
                      <div style={detailRowStyle}>
                        <span style={detailLabelStyle}>Performed By:</span>
                        <span style={detailValueStyle}>{entry.actorName || 'System'}</span>
                      </div>
                      
                      {/* Action Type */}
                      <div style={detailRowStyle}>
                        <span style={detailLabelStyle}>Action:</span>
                        <span style={detailValueStyle}>{formatActionName(entry.action)}</span>
                      </div>
                      
                      {/* Formatted Details - only show if there are visible fields */}
                      {entry.details && Object.entries(entry.details)
                        .filter(([key]) => formatDetailKey(key) !== null)
                        .map(([key, value]) => (
                          <div key={key} style={detailRowStyle}>
                            <span style={detailLabelStyle}>{formatDetailKey(key)}:</span>
                            <span style={detailValueStyle}>{formatDetailValue(key, value)}</span>
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Load More Button */}
            {hasMore && (
              <div style={loadMoreStyle}>
                <button 
                  style={loadMoreButtonStyle}
                  onClick={() => setDisplayCount(prev => prev + 50)}
                >
                  Load More ({filteredEntries.length - displayCount} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AuditLog;
